import { FC, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import { SpecialtyGroupExceptionStatusLabels } from "@app/exceptions/enums";
import NetworkSubmissionService from "@app/compliance-connect/network-submission/networkSubmissionService";
import LoadingSpinner from "@app/shared/LoadingSpinner";
import { formatDateCustom } from "@app/shared/formatDateTime";

interface OverviewRowProps {
  label: string;
  testId: string;
  value: string | number;
}

const OverviewRow: FC<OverviewRowProps> = ({ label, testId, value }) => (
  <div className="compliance-connect-network-profile__overview-row np-gray-border-bottom" data-testid={testId}>
    <span className="compliance-connect-network-profile__overview-label" data-testid={`${testId}-label`}>{label}</span>
    <span className="compliance-connect-network-profile__overview-value" data-testid={`${testId}-value`}>{value}</span>
  </div>
);

export const NetworkProfileOverview: FC = () => {
  const { submissionId } = useParams<{ submissionId?: string }>();
  const service = useMemo(() => new NetworkSubmissionService(), []);
  const numSubmissionId = Number(submissionId);

  const { data, isLoading } = useQuery({
    enabled: Number.isInteger(numSubmissionId),
    queryFn: ({ signal }) => service.getNetworkProfileOverview(numSubmissionId, signal),
    queryKey: ["ns-network-profile-overview", submissionId],
    staleTime: 5 * 60 * 1000,
  });

  if (isLoading) return <LoadingSpinner />;
  if (!data) return null;

  return (
    <div className="compliance-connect-network-profile__card" data-testid="network-profile-overview">
      <h4 className="compliance-connect-network-profile__card-title np-gray-border-bottom" data-testid="network-profile-overview-title">Overview</h4>
      <div className="compliance-connect-network-profile__overview" data-testid="network-profile-overview-rows">
        <OverviewRow label="Description" testId="network-profile-overview-row-description" value={data.description ?? ""} />
        <OverviewRow label="Line of Business" testId="network-profile-overview-row-line-of-business" value={data.lineOfBusiness ?? ""} />
        <OverviewRow label="Date Submitted" testId="network-profile-overview-row-date-submitted" value={formatDateCustom(data.dateSubmitted, "MMMM D, YYYY")} />
        <OverviewRow label="Status" testId="network-profile-overview-row-status" value={SpecialtyGroupExceptionStatusLabels[data.status] ?? data.status} />
        <OverviewRow label="Provider Count" testId="network-profile-overview-row-provider-count" value={data.providerCount.toLocaleString()} />
        <OverviewRow label="Adequacy Score" testId="network-profile-overview-row-adequacy-score" value={data.adequacyScore.toFixed(1)} />
        <OverviewRow label="Counties" testId="network-profile-overview-row-counties" value={data.counties} />
        <OverviewRow label="Specialties" testId="network-profile-overview-row-specialties" value={data.specialties} />
        <OverviewRow label="Membership" testId="network-profile-overview-row-membership" value={data.membership.toLocaleString()} />
      </div>
    </div>
  );
};
